import { type FC, useState, useEffect, useRef } from 'react';
import { HiSearch } from 'react-icons/hi';
import { TiDelete } from 'react-icons/ti';
import Flag from 'components/Flag';
import countryList from 'assets/data/countries';
import {
  Container,
  SearchContainer,
  Search,
  Placeholder,
  Country,
  CountryList,
  NoOptions
} from './styled';

type TCountry = (typeof countryList)[number];

type CountrySelectProps = {
  className?: string;
  countries: typeof countryList;
  selectedCountry?: TCountry | null;
  onSelect: (country: TCountry | null) => void;
};

const CountrySelect: FC<CountrySelectProps> = ({
  countries,
  selectedCountry,
  onSelect,
  className
}) => {
  const [searchValue, setSearchValue] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectedCountry && selectedCountry.name) {
      setSearchValue('');
    }
  }, [selectedCountry]);

  const matchingCountries = countries.filter((country) =>
    country.name.toLowerCase().startsWith(searchValue.toLowerCase())
  );

  return (
    <Container className={className}>
      <SearchContainer>
        <Search
          ref={searchRef}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        <Placeholder>
          {!searchValue &&
            (selectedCountry ? (
              <>
                <Flag size={44} code={selectedCountry.code} />
                {selectedCountry.name}
              </>
            ) : (
              'Search for a country'
            ))}
        </Placeholder>
        {searchValue || selectedCountry ? (
          <TiDelete
            onClick={() => {
              setSearchValue('');
              onSelect(null);
              searchRef.current?.focus();
            }}
          />
        ) : (
          <HiSearch />
        )}
      </SearchContainer>
      <CountryList>
        {matchingCountries.map((country) => (
          <Country
            key={country.code}
            onClick={() => {
              onSelect(country);
              setSearchValue('');
              searchRef.current?.focus();
            }}
            $selected={!!selectedCountry && country.code === selectedCountry.code}
          >
            <Flag size={44} code={country.code} />
            <div>{country.name}</div>
          </Country>
        ))}
        {matchingCountries.length === 0 && <NoOptions>No options</NoOptions>}
      </CountryList>
    </Container>
  );
};

export default CountrySelect;
